import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from './AuthContext';

const PromotionalContext = createContext();

export const usePromotional = () => {
  const context = useContext(PromotionalContext);
  if (!context) {
    throw new Error('usePromotional must be used within a PromotionalProvider');
  }
  return context;
};

export const PromotionalProvider = ({ children }) => {
  const [activePromotions, setActivePromotions] = useState([]);
  const [headerBanners, setHeaderBanners] = useState([]);
  const [popupPromotions, setPopupPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const latestRequestIdRef = React.useRef(0);

  // Fetch active promotions based on current exam context (not user's selected exams)
  const fetchActivePromotions = React.useCallback(async (currentExamId = null) => {
    try {
      const requestId = ++latestRequestIdRef.current;

      let query = supabase
        .from('promotions')
        .select('*, category_id, placement')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      const examFilter = [];
      examFilter.push('exam_id.is.null');
      if (currentExamId) {
        examFilter.push(`exam_id.eq."${currentExamId}"`);
      }

      if (examFilter.length > 0) {
        query = query.or(examFilter.join(','));
      }

      const { data, error } = await query;
      if (error) throw error;

      const nowDate = new Date();
      const filteredByDate =
        data?.filter((promotion) => {
          const validStart = !promotion.start_date || nowDate >= new Date(promotion.start_date);

          let validEnd = true;
          if (promotion.end_date) {
            const endDate = new Date(promotion.end_date);
            // Include the entire end date
            endDate.setHours(23, 59, 59, 999);
            validEnd = nowDate <= endDate;
          }

          return validStart && validEnd;
        }) || [];

      if (requestId === latestRequestIdRef.current) {
        setActivePromotions(filteredByDate);
        setHeaderBanners(filteredByDate.filter((p) => p.type === 'banner' || p.type === 'both'));
        setPopupPromotions(
          filteredByDate.filter(
            (p) =>
              (p.type === 'popup' || p.type === 'both') && p.placement !== 'premium_upgrade_popup'
          )
        );
      }
    } catch (error) {
      console.error('❌ Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPromotionsForExam = React.useCallback(
    async (examId) => {
      await fetchActivePromotions(examId);
    },
    [fetchActivePromotions]
  );

  const trackPromotionInteraction = React.useCallback(
    async (promotionId, actionType, placement, examId = null) => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        await supabase.from('promotion_analytics').insert({
          promotion_id: promotionId,
          user_id: user?.id || null,
          action_type: actionType,
          placement: placement,
          device_info: {
            userAgent: navigator.userAgent,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            timestamp: new Date().toISOString(),
            examId: examId,
          },
        });

        if (actionType === 'view' || actionType === 'click') {
          const today = new Date().toISOString().split('T')[0];
          await supabase.from('user_promotion_interactions').upsert(
            {
              user_id: user?.id,
              promotion_id: promotionId,
              last_shown_date: today,
              view_count: actionType === 'view' ? 1 : 0,
              click_count: actionType === 'click' ? 1 : 0,
            },
            {
              onConflict: 'user_id,promotion_id',
            }
          );
        }
      } catch (error) {
        console.error('Error tracking promotion interaction:', error);
      }
    },
    []
  );

  const shouldShowPopup = React.useCallback(async (promotionId) => {
    // Bypass once-per-day check during local development/testing
    if (window.location.hostname === 'localhost') {
      return true;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('user_promotion_interactions')
        .select('last_shown_date')
        .eq('user_id', user.id)
        .eq('promotion_id', promotionId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !data || data.last_shown_date !== today;
    } catch (error) {
      console.error('Error checking popup status:', error);
      return false;
    }
  }, []);

  const getAvailablePopups = React.useCallback(
    async (selectedExam = null) => {
      const availablePopups = [];
      let filteringPopups = popupPromotions.filter((p) => {
        // Basic exam/category filtering
        const matchesExam = !p.exam_id || p.exam_id === selectedExam?.id;
        const matchesCategory =
          p.exam_id && p.exam_id === selectedExam?.id
            ? true
            : !p.category_id || p.category_id === selectedExam?.category_id;
        return matchesExam && matchesCategory;
      });

      // If there is any custom popup targeting the selected exam, exclude default popups (where exam_id is null)
      const hasCustomExamPopup =
        selectedExam && filteringPopups.some((p) => p.exam_id === selectedExam.id);
      if (hasCustomExamPopup) {
        filteringPopups = filteringPopups.filter((p) => p.exam_id === selectedExam.id);
      }

      for (const promotion of filteringPopups) {
        const shouldShow = await shouldShowPopup(promotion.id);
        if (shouldShow) availablePopups.push(promotion);
      }
      return availablePopups;
    },
    [popupPromotions, shouldShowPopup]
  );

  useEffect(() => {
    fetchActivePromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      activePromotions,
      headerBanners,
      popupPromotions,
      loading,
      fetchPromotionsForExam,
      fetchActivePromotions,
      trackPromotionInteraction,
      getAvailablePopups,
    }),
    [
      activePromotions,
      headerBanners,
      popupPromotions,
      loading,
      fetchPromotionsForExam,
      fetchActivePromotions,
      trackPromotionInteraction,
      getAvailablePopups,
    ]
  );

  return <PromotionalContext.Provider value={value}>{children}</PromotionalContext.Provider>;
};

export default PromotionalContext;
